import React, { useState, useEffect, useContext } from 'react';
import axios from 'axios';
import { AppContext } from '../../App';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { 
  FaUser, FaBuilding, FaEnvelope, FaPhone, FaClock, FaSearch, 
  FaEye, FaEdit, FaTrash, FaUserCheck, FaUserTimes, FaUserCog,
  FaMapMarkerAlt, FaGlobe, FaIdCard, FaShoppingBag, FaBriefcase,
  FaFileAlt, FaLink, FaUsers, FaClipboardList, FaCheckCircle
} from 'react-icons/fa';
import AdminLayout from '../../components/AdminLayout';

export default function UsersManagement() {
  const { API, user } = useContext(AppContext);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [roleFilter, setRoleFilter] = useState('all');
  
  // View/Edit dialog
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [editData, setEditData] = useState({});

  useEffect(() => {
    if (user) {
      fetchUsers();
    }
  }, [user]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/auth/admin/all-users?admin_id=${user.id}`);
      setUsers(response.data);
    } catch (error) {
      console.error('Error fetching users:', error);
      toast.error('Erreur lors du chargement des utilisateurs');
    } finally {
      setLoading(false);
    }
  };

  const openUserDetails = (userData) => {
    setSelectedUser(userData);
    setEditData({
      name: userData.name || '',
      email: userData.email || '',
      phone: userData.phone || '',
      civility: userData.civility || '',
      profession: userData.profession || '',
      company_name: userData.company_name || userData.company || '',
      activity_type: userData.activity_type || '',
      siret: userData.siret || '',
      tva_number: userData.tva_number || '',
      website: userData.website || '',
      company_size: userData.company_size || '',
      address: userData.address || '',
      postal_code: userData.postal_code || '',
      city: userData.city || '',
      country: userData.country || 'France',
      role: userData.role || 'user',
      account_status: userData.account_status || 'pending'
    });
    setEditMode(false);
    setDetailsDialogOpen(true);
  };

  const handleUpdateUser = async () => {
    if (!selectedUser) return;

    try {
      await axios.put(`${API}/auth/admin/update-user/${selectedUser.id}?admin_id=${user.id}`, editData);
      toast.success('Utilisateur mis a jour avec succes');
      setDetailsDialogOpen(false);
      fetchUsers();
    } catch (error) {
      console.error('Error updating user:', error);
      toast.error('Erreur lors de la mise a jour');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    if (!window.confirm(`Etes-vous sur de vouloir supprimer le compte de ${userName} ? Cette action est irreversible.`)) {
      return;
    }

    try {
      await axios.delete(`${API}/auth/admin/delete-user/${userId}?admin_id=${user.id}`);
      toast.success('Utilisateur supprime avec succes');
      fetchUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const handleApprove = async (userId, userName) => {
    try {
      await axios.post(`${API}/auth/admin/approve-user/${userId}?admin_id=${user.id}`);
      toast.success(`Compte de ${userName} approuve`);
      fetchUsers();
    } catch (error) {
      console.error('Error approving user:', error);
      toast.error("Erreur lors de l'approbation");
    }
  };

  // Filter users
  const filteredUsers = users.filter(u => {
    const matchesSearch = 
      u.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.company?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      u.siret?.includes(searchTerm) ||
      u.phone?.includes(searchTerm);
    
    const matchesStatus = statusFilter === 'all' || u.account_status === statusFilter;
    const matchesRole = roleFilter === 'all' || u.role === roleFilter;
    
    return matchesSearch && matchesStatus && matchesRole;
  });

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return <Badge className="bg-green-100 text-green-800">Approuvé</Badge>;
      case 'pending':
        return <Badge className="bg-yellow-100 text-yellow-800">En attente</Badge>;
      case 'rejected':
        return <Badge className="bg-red-100 text-red-800">Rejeté</Badge>;
      case 'suspended':
        return <Badge className="bg-gray-100 text-gray-800">Suspendu</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">{status}</Badge>;
    }
  };

  const getRoleBadge = (role) => {
    switch (role) {
      case 'admin':
        return <Badge className="bg-purple-100 text-purple-800">Admin</Badge>;
      case 'supplier':
      case 'fournisseur':
        return <Badge className="bg-blue-100 text-blue-800">Fournisseur</Badge>;
      case 'pro':
        return <Badge className="bg-indigo-100 text-indigo-800">Pro</Badge>;
      default:
        return <Badge className="bg-gray-100 text-gray-800">Client</Badge>;
    }
  };

  // Labels for display
  const accountTypeLabels = {
    'client': 'Client',
    'supplier': 'Fournisseur',
    'pro': 'Professionnel',
    'both': 'Client & Fournisseur'
  };

  const needsLabels = {
    'location': 'Location de materiel',
    'achat_neuf': 'Achat de materiel neuf',
    'achat_occasion': 'Achat de materiel occasion',
    'services': 'Services geotechniques',
    'formation': 'Formation',
    'conseil': 'Conseil technique'
  };

  const activityTypeLabels = {
    'geotechnique': 'Geotechnique',
    'btp': 'BTP / Construction',
    'bureau_etude': "Bureau d'etude",
    'laboratoire': 'Laboratoire',
    'foreur': 'Foreur',
    'negoce': 'Negoce',
    'fabricant': 'Fabricant',
    'autre': 'Autre'
  };

  return (
    <AdminLayout>
      <div className="p-4 space-y-4">
        {/* Header */}
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-[#0A2540]">Gestion des Utilisateurs</h1>
            <p className="text-gray-500 text-sm">
              {users.length} utilisateur(s) au total
            </p>
          </div>
          <Button onClick={fetchUsers} variant="outline" size="sm">
            Actualiser
          </Button>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-col md:flex-row gap-3">
              <div className="relative flex-1">
                <FaSearch className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm" />
                <Input
                  placeholder="Rechercher par nom, email, SIRET..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-9 h-9 text-sm"
                />
              </div>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full md:w-40 h-9 text-sm">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="approved">Approuvé</SelectItem>
                  <SelectItem value="pending">En attente</SelectItem>
                  <SelectItem value="rejected">Rejeté</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full md:w-40 h-9 text-sm">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous</SelectItem>
                  <SelectItem value="admin">Admin</SelectItem>
                  <SelectItem value="supplier">Fournisseur</SelectItem>
                  <SelectItem value="user">Client</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        {loading ? (
          <Card>
            <CardContent className="p-12 text-center">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#635BFF] mx-auto"></div>
              <p className="mt-4 text-gray-500">Chargement...</p>
            </CardContent>
          </Card>
        ) : (
          <Card>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="p-3 text-left font-semibold text-gray-600">Utilisateur</th>
                      <th className="p-3 text-left font-semibold text-gray-600">Contact</th>
                      <th className="p-3 text-left font-semibold text-gray-600">Entreprise</th>
                      <th className="p-3 text-left font-semibold text-gray-600">Statut</th>
                      <th className="p-3 text-left font-semibold text-gray-600">Inscription</th>
                      <th className="p-3 text-center font-semibold text-gray-600">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y">
                    {filteredUsers.map(u => (
                      <tr key={u.id} className="hover:bg-gray-50">
                        <td className="p-3">
                          <div className="flex items-center gap-2">
                            <div className="w-8 h-8 bg-[#635BFF]/10 rounded-full flex items-center justify-center flex-shrink-0">
                              <FaUser className="text-[#635BFF] text-xs" />
                            </div>
                            <div>
                              <p className="font-medium text-[#0A2540]">
                                {u.civility && `${u.civility} `}{u.name}
                              </p>
                              <p className="text-xs text-gray-400">{u.profession || u.role}</p>
                            </div>
                          </div>
                        </td>
                        <td className="p-3">
                          <div className="text-xs">
                            <p className="text-gray-600">{u.email}</p>
                            {u.phone && <p className="text-gray-400">{u.phone}</p>}
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-xs text-gray-600">{u.company_name || u.company || '-'}</p>
                          {u.siret && <p className="text-xs text-gray-400">SIRET: {u.siret}</p>}
                        </td>
                        <td className="p-3">
                          <div className="flex flex-col gap-1">
                            {getRoleBadge(u.role)}
                            {getStatusBadge(u.account_status)}
                          </div>
                        </td>
                        <td className="p-3">
                          <p className="text-xs text-gray-500">
                            {new Date(u.created_at).toLocaleDateString('fr-FR')}
                          </p>
                        </td>
                        <td className="p-3">
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 w-7 p-0"
                              onClick={() => openUserDetails(u)}
                              title="Voir details"
                            >
                              <FaEye className="text-xs" />
                            </Button>
                            {u.account_status === 'pending' && (
                              <Button
                                size="sm"
                                className="bg-green-600 hover:bg-green-700 h-7 w-7 p-0"
                                onClick={() => handleApprove(u.id, u.name)}
                                title="Approuver"
                              >
                                <FaUserCheck className="text-xs" />
                              </Button>
                            )}
                            {u.role !== 'admin' && (
                              <Button
                                size="sm"
                                variant="destructive"
                                className="h-7 w-7 p-0"
                                onClick={() => handleDeleteUser(u.id, u.name)}
                                title="Supprimer"
                              >
                                <FaTrash className="text-xs" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              
              {filteredUsers.length === 0 && (
                <div className="p-12 text-center">
                  <FaUserCog className="text-5xl text-gray-300 mx-auto mb-4" />
                  <p className="text-gray-500">Aucun utilisateur trouve</p>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* User Details Dialog - FICHE COMPLETE */}
        <Dialog open={detailsDialogOpen} onOpenChange={setDetailsDialogOpen}>
          <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <FaUserCog className="text-[#635BFF]" />
                {editMode ? "Modifier l'utilisateur" : "Fiche Utilisateur Complete"}
              </DialogTitle>
            </DialogHeader>
            
            {selectedUser && (
              <div className="space-y-6">
                {/* Header Card */}
                <div className="bg-gradient-to-r from-[#635BFF]/10 to-[#00D4FF]/10 p-4 rounded-xl">
                  <div className="flex items-center gap-4">
                    <div className="w-14 h-14 bg-white rounded-full flex items-center justify-center shadow">
                      <FaUser className="text-[#635BFF] text-xl" />
                    </div>
                    <div className="flex-1">
                      <h3 className="text-lg font-bold text-[#0A2540]">
                        {selectedUser.civility && `${selectedUser.civility} `}{selectedUser.name}
                      </h3>
                      <p className="text-sm text-gray-500">{selectedUser.profession || '-'}</p>
                      <div className="flex gap-2 mt-1">
                        {getRoleBadge(selectedUser.role)}
                        {getStatusBadge(selectedUser.account_status)}
                      </div>
                    </div>
                  </div>
                </div>

                {/* === SECTION 1: IDENTITE === */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FaUser className="text-[#635BFF]" /> Identite
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Civilite</Label>
                      {editMode ? (
                        <Select value={editData.civility} onValueChange={(v) => setEditData({...editData, civility: v})}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="M.">M.</SelectItem>
                            <SelectItem value="Mme">Mme</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium">{selectedUser.civility || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Nom complet</Label>
                      {editMode ? (
                        <Input value={editData.name} onChange={(e) => setEditData({...editData, name: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.name}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Profession</Label>
                      {editMode ? (
                        <Input value={editData.profession} onChange={(e) => setEditData({...editData, profession: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.profession || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* === SECTION 2: CONTACT === */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FaEnvelope className="text-[#00D4FF]" /> Contact
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Email</Label>
                      {editMode ? (
                        <Input type="email" value={editData.email} onChange={(e) => setEditData({...editData, email: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.email}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Telephone</Label>
                      {editMode ? (
                        <Input value={editData.phone} onChange={(e) => setEditData({...editData, phone: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.phone || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* === SECTION 3: ENTREPRISE === */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FaBuilding className="text-[#0A2540]" /> Entreprise
                  </h4>
                  {selectedUser.has_company || selectedUser.company_name || selectedUser.company ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <Label className="text-xs text-gray-500">Nom de l'entreprise</Label>
                        {editMode ? (
                          <Input value={editData.company_name} onChange={(e) => setEditData({...editData, company_name: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium">{selectedUser.company_name || selectedUser.company || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Type d'activite</Label>
                        {editMode ? (
                          <Input value={editData.activity_type} onChange={(e) => setEditData({...editData, activity_type: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium">{activityTypeLabels[selectedUser.activity_type] || selectedUser.activity_type || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">SIRET</Label>
                        {editMode ? (
                          <Input value={editData.siret} onChange={(e) => setEditData({...editData, siret: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium font-mono">{selectedUser.siret || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">N° TVA Intracommunautaire</Label>
                        {editMode ? (
                          <Input value={editData.tva_number} onChange={(e) => setEditData({...editData, tva_number: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium font-mono">{selectedUser.tva_number || '-'}</p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Site web</Label>
                        {editMode ? (
                          <Input value={editData.website} onChange={(e) => setEditData({...editData, website: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium">
                            {selectedUser.website ? (
                              <a href={selectedUser.website.startsWith('http') ? selectedUser.website : `https://${selectedUser.website}`} target="_blank" rel="noopener noreferrer" className="text-[#635BFF] hover:underline">
                                {selectedUser.website}
                              </a>
                            ) : '-'}
                          </p>
                        )}
                      </div>
                      <div>
                        <Label className="text-xs text-gray-500">Taille de l'entreprise</Label>
                        {editMode ? (
                          <Input value={editData.company_size} onChange={(e) => setEditData({...editData, company_size: e.target.value})} className="h-9" />
                        ) : (
                          <p className="font-medium">{selectedUser.company_size || '-'}</p>
                        )}
                      </div>
                    </div>
                  ) : (
                    <p className="text-gray-500 italic">Pas d'entreprise renseignee</p>
                  )}
                </div>

                {/* === SECTION 4: ADRESSE === */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FaMapMarkerAlt className="text-red-500" /> Adresse
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="md:col-span-2">
                      <Label className="text-xs text-gray-500">Adresse</Label>
                      {editMode ? (
                        <Input value={editData.address} onChange={(e) => setEditData({...editData, address: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.address || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Code postal</Label>
                      {editMode ? (
                        <Input value={editData.postal_code} onChange={(e) => setEditData({...editData, postal_code: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.postal_code || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Ville</Label>
                      {editMode ? (
                        <Input value={editData.city} onChange={(e) => setEditData({...editData, city: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.city || '-'}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Pays</Label>
                      {editMode ? (
                        <Input value={editData.country} onChange={(e) => setEditData({...editData, country: e.target.value})} className="h-9" />
                      ) : (
                        <p className="font-medium">{selectedUser.country || '-'}</p>
                      )}
                    </div>
                  </div>
                </div>

                {/* === SECTION 5: TYPE DE COMPTE & BESOINS === */}
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                      <FaUsers className="text-[#635BFF]" /> Type(s) de compte
                    </h4>
                    {selectedUser.account_types && selectedUser.account_types.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.account_types.map((type, idx) => (
                          <Badge key={idx} className="bg-[#635BFF]/10 text-[#635BFF]">
                            {accountTypeLabels[type] || type}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic text-sm">Non renseigne</p>
                    )}
                  </div>

                  <div className="border rounded-lg p-4">
                    <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                      <FaClipboardList className="text-green-500" /> Besoins
                    </h4>
                    {selectedUser.needs && selectedUser.needs.length > 0 ? (
                      <div className="flex flex-wrap gap-2">
                        {selectedUser.needs.map((need, idx) => (
                          <Badge key={idx} className="bg-green-50 text-green-700">
                            {needsLabels[need] || need}
                          </Badge>
                        ))}
                      </div>
                    ) : (
                      <p className="text-gray-500 italic text-sm">Non renseigne</p>
                    )}
                  </div>
                </div>

                {/* === SECTION 6: COMPTE & ADMINISTRATION === */}
                <div className="border rounded-lg p-4">
                  <h4 className="font-semibold text-[#0A2540] mb-3 flex items-center gap-2">
                    <FaIdCard className="text-purple-500" /> Administration
                  </h4>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div>
                      <Label className="text-xs text-gray-500">Role</Label>
                      {editMode ? (
                        <Select value={editData.role} onValueChange={(v) => setEditData({...editData, role: v})}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="user">Client</SelectItem>
                            <SelectItem value="supplier">Fournisseur</SelectItem>
                            <SelectItem value="admin">Admin</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium capitalize">{selectedUser.role}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Statut du compte</Label>
                      {editMode ? (
                        <Select value={editData.account_status} onValueChange={(v) => setEditData({...editData, account_status: v})}>
                          <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
                          <SelectContent>
                            <SelectItem value="pending">En attente</SelectItem>
                            <SelectItem value="approved">Approuvé</SelectItem>
                            <SelectItem value="rejected">Rejeté</SelectItem>
                            <SelectItem value="suspended">Suspendu</SelectItem>
                          </SelectContent>
                        </Select>
                      ) : (
                        <p className="font-medium capitalize">{selectedUser.account_status === 'approved' ? 'Approuvé' : selectedUser.account_status === 'pending' ? 'En attente' : selectedUser.account_status}</p>
                      )}
                    </div>
                    <div>
                      <Label className="text-xs text-gray-500">Date d'inscription</Label>
                      <p className="font-medium">
                        {new Date(selectedUser.created_at).toLocaleDateString('fr-FR', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                </div>

                {/* ID Section */}
                <div className="bg-gray-50 p-3 rounded-lg">
                  <Label className="text-xs text-gray-500">ID Utilisateur</Label>
                  <p className="font-mono text-xs mt-1">{selectedUser.id}</p>
                </div>
              </div>
            )}

            <DialogFooter className="gap-2">
              {editMode ? (
                <>
                  <Button variant="outline" onClick={() => setEditMode(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleUpdateUser} className="bg-[#635BFF]">
                    Enregistrer
                  </Button>
                </>
              ) : (
                <>
                  <Button variant="outline" onClick={() => setDetailsDialogOpen(false)}>
                    Fermer
                  </Button>
                  <Button onClick={() => setEditMode(true)} className="bg-[#635BFF]">
                    <FaEdit className="mr-2" /> Modifier
                  </Button>
                </>
              )}
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </AdminLayout>
  );
}
